import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '@proctoring/api-client';
import type { InterviewState } from '@proctoring/shared';
import { useApi } from '../auth/AuthProvider.js';

/** Query keys in one place so mutations can invalidate without guessing at shapes. */
export const queryKeys = {
  candidateProfile: ['candidate', 'me'] as const,
  interviewerInterviews: (state?: InterviewState) => ['interviewer', 'interviews', state] as const,
  interviewerInterview: (id: string) => ['interviewer', 'interview', id] as const,
  interviewerTelemetry: (id: string) => ['interviewer', 'telemetry', id] as const,
  health: ['health'] as const,
};

export function useCandidateProfile() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.candidateProfile,
    queryFn: ({ signal }) => api.candidateProfile(signal),
  });
}

export function useInterviewerInterviews() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.interviewerInterviews(),
    queryFn: ({ signal }) => api.interviewerInterviews({}, signal),
  });
}

export function useInterviewerInterview(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.interviewerInterview(id),
    queryFn: ({ signal }) => api.interviewerInterview(id, signal),
    enabled: id.length > 0,
  });
}

export function useInterviewerTelemetry(id: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.interviewerTelemetry(id),
    queryFn: ({ signal }) => api.interviewerTelemetry(id, signal),
    enabled: enabled && id.length > 0,
  });
}

export function useEndInterview(id: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.endInterview(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['interviewer'] });
    },
  });
}

/**
 * Downloads a telemetry export.
 *
 * Done through the client rather than an `<a href>` because the endpoint is bearer
 * authenticated — a plain link would arrive without the JWT and 401.
 */
export async function downloadTelemetryExport(
  api: ApiClient,
  interviewId: string,
  format: 'json' | 'csv',
  scope: 'interviewer' | 'admin' = 'interviewer',
): Promise<void> {
  const blob =
    scope === 'admin'
      ? await api.adminTelemetryExport(interviewId, format)
      : await api.interviewerTelemetryExport(interviewId, format);

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `telemetry-${interviewId}.${format}`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
